// src/data/banners.ts
// -------------------
// Repository for the `banners` table — the top-of-page announcement strip + the
// admin manager. Typed via getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type BannerRow = Database["public"]["Tables"]["banners"]["Row"];
type BannerInsert = Database["public"]["Tables"]["banners"]["Insert"];
type BannerUpdate = Database["public"]["Tables"]["banners"]["Update"];

// Active banners (the strip filters these by audience/state/expiry client-side).
export async function listActiveBanners(): Promise<BannerRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("banners").select("*").eq("active", true);
  if (error) {
    logger.warn("listActiveBanners failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as BannerRow[];
}

// All banners, active first (admin manager list).
export async function listAllBanners(): Promise<BannerRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c.from("banners").select("*").order("active", { ascending: false });
  if (error) {
    logger.warn("listAllBanners failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as BannerRow[];
}

export async function insertBanner(row: object): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("banners").insert(row as BannerInsert);
  if (error) logger.warn("insertBanner failed:", error.message);
  return { error };
}

export async function updateBannerById(
  id: string | number,
  patch: object
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("banners")
    .update(patch as BannerUpdate)
    .eq("id", id as string);
  if (error) logger.warn("updateBannerById failed:", error.message);
  return { error };
}
